import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import Title from 'Components/ComponentLibrary/Atoms/Text/Title';
import Subtitle from 'Components/ComponentLibrary/Atoms/Text/Subtitle';
import fr from 'facet-rest';
import { hasRelationship } from 'Helpers/Affordances';
import TextField from 'Components/ComponentLibrary/Components/Uncategorized/TextField';
import { wizardClasses } from 'Views/Page/Content/Templates/WizardTemplates/wizardCommonStyles';
import WizardStandardHeader from 'Components/ComponentLibrary/Components/Uncategorized/WizardStandardHeader';
import { useHistory } from 'react-router-dom';
import FundingInstructionsCollapsibleCardContainer
  from 'Components/Modals/AccountFundingInstructions/FundingInstructionsCollapsibleCard/FundingInstructionsCollapsibleCardContainer';
import FinancialInstitutionCard
  from 'Components/ComponentLibrary/Organisms/FinancialInstitutionCard/FinancialInstitutionCard';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';

const useStyles = buildUseStyles('ACATInstitutionSelectionTemplate', (theme) => ({
  root: {
    ...wizardClasses.root(theme),
    margin: 0,
  },
  container: {
    ...wizardClasses.container(theme),
  },
  content: {
    ...wizardClasses.content(theme),
  },
  header: {
    maxWidth: theme.dimensions.md,
    width: '100%',
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.common.white,
    zIndex: 1,
    padding: theme.spacing(0, 2),
  },
  gap: {
    marginBottom: theme.spacing(2),
  },
  input: {
    padding: theme.spacing(2),
  },
  cardContainer: {
    width: '100%',
    padding: theme.spacing(0, 2),
  },
  card: {
    margin: theme.spacing(2, 0),
  },
}));

const ACATInstitutionSelectionTemplate = ({
  className,
  resource,
}) => {
  const {
    pageContentResource,
    setPageContentCollection,
    loadPageContentLink,
  } = usePageContentContext();
  const [search, setSearch] = useState('');
  const theme = useTheme();
  const classes = useStyles(theme);
  const institutions = pageContentResource.meta().field('institutionID').options().list();
  const history = useHistory();
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  const handleOnClick = async (id) => {
    logStandardizedEvent(categories.accountFunding, eventTypes.clickButton, 'institution selected for ACAT transfer', {
      eventProperties: {
        institutionId: id,
      },
    });

    resource.attr('institutionID', id);
    const nextPageColl = await resource.submit();
    setPageContentCollection(nextPageColl);
  };

  const loadManualStep = () => {
    logStandardizedEvent(categories.accountFunding, eventTypes.clickButton, 'dont see your ACAT institution clicked');

    loadPageContentLink(resource.relationship(fr.relationships.manualTransferStep.name).link('related'));
  };

  const onClose = async () => {
    if (hasRelationship(resource, fr.relationships.redirectOnClose.name)) {
      await loadPageContentLink(resource.relationship(fr.relationships.redirectOnClose.name).link('related'));
    } else {
      // Fallthrough case - unsure where to navigate to go to dashboard
      history.push('/');
    }
  };

  const showManualCard = hasRelationship(resource, fr.relationships.manualTransferStep.name);
  const fundingInstructionsRelationship = resource.relationship(fr.relationships.fundingInstructions.name)?.link('related');
  return (
    <Box className={classNames(classes.root, className)}>
      <WizardStandardHeader
        color={'primary'}
        title={resource.attr('moduleName')}
        onClose={onClose}
      />
      <Box className={classes.header}>
        <Title className={classes.gap} text={pageContentResource.attr('title')} test={'wizard-step-title'} />
        <Subtitle className={classes.gap} text={pageContentResource.attr('subtitle')} />
        {fundingInstructionsRelationship && (
          <FundingInstructionsCollapsibleCardContainer
            className={classes.gap}
            defaultExpanded
            title={'Schedule Deposit'}
            fundingInstructionsLink={fundingInstructionsRelationship}
          />
        )}
      </Box>
      <Box className={classes.container}>
        <Box className={classes.content}>
          <Box className={classes.cardContainer}>
            <TextField
              placeholder={'Search for ACAT Institution'}
              onChange={val => setSearch(val.toUpperCase())}
              data-test="institution-search"
              size="small"
              InputProps={{
                classes: {
                  input: classes.input,
                },
              }}
            />
            {institutions.filter(institution => institution.attr('prompt').toUpperCase().includes(search)).map(institution => (
              <>
                <FinancialInstitutionCard
                  className={classes.card}
                  logo={institution.attr('media')}
                  subText={institution.attr('prompt')}
                  onClick={() => handleOnClick(institution.attr('id'))}
                />
              </>
            ))}
            {showManualCard && (
              <FinancialInstitutionCard
                name={'Don\'t see your institution?'}
                className={classes.card}
                variant={'primary'}
                onClick={loadManualStep}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

ACATInstitutionSelectionTemplate.defaultProps = {
  className: '',
};

ACATInstitutionSelectionTemplate.propTypes = {
  className: PropTypes.string,
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};

export default ACATInstitutionSelectionTemplate;
